import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/common/Card";
import { Button } from "@/components/common/Button";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ComboBox } from "@/components/common/ComboBox";
import { Modal } from "@/components/common/Modal";
import { journalService } from "@/services/journal";
import { settingsService } from "@/services/settings";
import { JournalEntry, JournalTypes, DropdownItem, ValidationEvent } from "@/types";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  AlertTriangle,
  AlertCircle,
  Download,
  Save,
  RotateCcw,
} from "lucide-react";
import { exportJournalToExcel } from "@/utils/exportJournalToExcel";
import { errorMessage } from "@/utils/errorMessage";
import { getBuildBlockingErrors, withCurrentBuildErrors } from "@/utils/buildErrors";

// Settings key for Posting Journal Type
const POSTING_JOURNAL_TYPE_KEY = "POSTING_JOURNAL_TYPE";
const POSTING_JOURNAL_TYPE_DEFAULT = "CRMJ";
const DEFAULT_TAX_ID_KEY = "DEFAULT_TAX_ID";
const DEFAULT_TAX_ID_DEFAULT = "G1 Goods and Services Tax";

// Column configuration for the journal posting grid
interface ColumnConfig {
  key: string;
  label: string;
  minWidth: number;
  defaultWidth: number;
  align: "left" | "right";
  sticky?: boolean; // Whether this column should be frozen on the left
}

const COLUMN_CONFIGS: ColumnConfig[] = [
  {
    key: "actions",
    label: "Actions",
    minWidth: 80,
    defaultWidth: 112,
    align: "left",
    sticky: true,
  },
  { key: "row", label: "Row", minWidth: 40, defaultWidth: 64, align: "left", sticky: true },
  {
    key: "journalType",
    label: "Journal Type",
    minWidth: 100,
    defaultWidth: 128,
    align: "left",
    sticky: true,
  },
  { key: "memo", label: "Memo", minWidth: 150, defaultWidth: 280, align: "left", sticky: true },
  {
    key: "accountNumber",
    label: "Account #",
    minWidth: 120,
    defaultWidth: 180,
    align: "left",
    sticky: true,
  },
  { key: "departmentId", label: "Department ID", minWidth: 120, defaultWidth: 208, align: "left" },
  { key: "projectId", label: "Project ID", minWidth: 120, defaultWidth: 224, align: "left" },
  { key: "classId", label: "Class ID", minWidth: 120, defaultWidth: 224, align: "left" },
  { key: "pfpCode", label: "PFP Code", minWidth: 120, defaultWidth: 208, align: "left" },
  {
    key: "contributionCode",
    label: "Contribution Code",
    minWidth: 120,
    defaultWidth: 224,
    align: "left",
  },
  { key: "debit", label: "Debit", minWidth: 140, defaultWidth: 200, align: "right" },
  { key: "credit", label: "Credit", minWidth: 140, defaultWidth: 200, align: "right" },
  { key: "baseDebit", label: "Base Debit", minWidth: 140, defaultWidth: 200, align: "right" },
  { key: "baseCredit", label: "Base Credit", minWidth: 140, defaultWidth: 200, align: "right" },
  { key: "taxId", label: "Tax ID", minWidth: 180, defaultWidth: 240, align: "left" },
  { key: "taxAmount", label: "Tax Amount", minWidth: 140, defaultWidth: 200, align: "right" },
];

// Get the last sticky column key
const LAST_STICKY_KEY = COLUMN_CONFIGS.filter((c) => c.sticky).slice(-1)[0]?.key;

// Calculate left positions for sticky columns
function getStickyLeftPositions(columnWidths: Record<string, number>): Record<string, number> {
  const positions: Record<string, number> = {};
  let cumulativeLeft = 0;

  for (const col of COLUMN_CONFIGS) {
    if (col.sticky) {
      positions[col.key] = cumulativeLeft;
      cumulativeLeft += columnWidths[col.key] || col.defaultWidth;
    }
  }

  return positions;
}

/**
 * Round a number to 2 decimal places for financial calculations
 * Uses Math.round to avoid floating-point precision issues
 */
function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

function calculateBaseAmount(
  amount: number | null | undefined,
  taxAmount: number | null | undefined,
): number {
  const lineAmount = amount ?? 0;
  if (lineAmount <= 0) {
    return 0;
  }

  return roundToTwoDecimals(lineAmount + Math.abs(taxAmount ?? 0));
}

function calculateDisplayedTaxAmount(taxAmount: number | null | undefined): number {
  return roundToTwoDecimals(Math.abs(taxAmount ?? 0));
}

/**
 * Validate a data row with all business rules
 * Migrated from ValidateDataRow() in original C# code
 * Returns validation events and may modify entries for certain rules
 * @param validAccountNumbers - Set of valid account numbers from the accounts table (optional, for account existence validation)
 * @param defaultTaxId - The default Tax ID to apply for accounts 451000 and 857000
 */
function validateDataRow(
  entry: JournalEntry,
  rowIndex: number,
  journalEntries: JournalEntry[],
  validAccountNumbers?: Set<string>,
  defaultTaxId: string = DEFAULT_TAX_ID_DEFAULT,
): ValidationEvent[] {
  const validationEvents: ValidationEvent[] = [];

  // ===========================================
  // Accrued Income 131000 (Legacies) Check
  // ===========================================
  if (
    entry.journalType === JournalTypes.Bank &&
    entry.accountNumber === "131000" &&
    entry.departmentId !== "24"
  ) {
    validationEvents.push({
      severity: "error",
      message: `Row ${rowIndex}: Accrued Income 131000 (Legacies) Rule, if the account is 131000 then the department must be 24 Gifts in Will.`,
      field: "departmentId",
    });
  }

  // ===========================================
  // International Soft Credits Rec Batches
  // ===========================================
  if (entry.journalType === JournalTypes.Revenue && entry.accountNumber === "415000") {
    // Check for a matching debit entry in Expense or Bank journal type with the same amount and required fields populated
    const matchingDebit = journalEntries.find(
      (e) =>
        (e.journalType === JournalTypes.Expense || e.journalType === JournalTypes.Bank) &&
        e.debit !== null &&
        e.debit === entry.credit,
    );

    if (!matchingDebit) {
      // No exact match found - check all Bank/Expense entries and update them
      for (const journal of journalEntries) {
        if (
          journal.journalType === JournalTypes.Bank ||
          journal.journalType === JournalTypes.Expense
        ) {
          const matchedIndex = journalEntries.indexOf(journal) + 1;

          // Modify the entry
          journal.journalType = JournalTypes.Expense;
          journal.accountNumber = "912000";

          if (
            !journal.departmentId?.trim() ||
            !journal.projectId?.trim() ||
            !journal.classId?.trim() ||
            !journal.pfpCode?.trim() ||
            !journal.contributionCode?.trim()
          ) {
            validationEvents.push({
              severity: "warning",
              message: `Row ${matchedIndex}: International Soft Credits Rule, the debit entry found and the journal type has been changed to ${journal.journalType}, account number amended to 912000. Fields (Department, Project, Class, PFP, Contribution) will now need to be populated to enable posting.`,
              field: "journalType",
            });
          }
        }
      }
    } else {
      const matchedIndex = journalEntries.indexOf(matchingDebit) + 1;

      if (matchingDebit.journalType !== JournalTypes.Expense) {
        // Modify the matching entry
        matchingDebit.journalType = JournalTypes.Expense;
        matchingDebit.accountNumber = "912000";

        if (
          !matchingDebit.departmentId?.trim() ||
          !matchingDebit.projectId?.trim() ||
          !matchingDebit.classId?.trim() ||
          !matchingDebit.pfpCode?.trim() ||
          !matchingDebit.contributionCode?.trim()
        ) {
          validationEvents.push({
            severity: "warning",
            message: `Row ${matchedIndex}: International Soft Credits Rule, the debit entry found and the journal type has been changed to ${matchingDebit.journalType}, account number amended to 912000. Fields (Department, Project, Class, PFP, Contribution) will now need to be populated to enable posting.`,
            field: "journalType",
          });
        }
      } else {
        if (
          !matchingDebit.departmentId?.trim() ||
          !matchingDebit.projectId?.trim() ||
          !matchingDebit.classId?.trim() ||
          !matchingDebit.pfpCode?.trim() ||
          !matchingDebit.contributionCode?.trim()
        ) {
          validationEvents.push({
            severity: "error",
            message: `Row ${matchedIndex}: International Soft Credits Rule, the debit entry found with journal type ${matchingDebit.journalType} and requires fields (Department, Project, Class, PFP, Contribution) populated.`,
            field: "departmentId",
          });
        }
      }
    }
  }

  // ===========================================
  // Pro-bono Rec Batches
  // ===========================================
  if (entry.journalType === JournalTypes.Revenue && entry.accountNumber === "421000") {
    // Bank and Revenue rows are built from independent aggregations of the same
    // opportunities (Bank groups by donation channel/payment type/stage; Revenue
    // groups by dimension codes), so a Revenue row's amount will rarely equal any
    // single Bank/Expense row's amount even when the batch balances overall.
    // Match on shared source opportunities instead. Fall back to amount equality
    // for rows with no opportunity linkage (e.g. manually added rows).
    const entryOppIds = new Set(entry.opportunityIds ?? []);
    const matchingDebits =
      entryOppIds.size > 0
        ? journalEntries.filter(
            (e) =>
              (e.journalType === JournalTypes.Expense || e.journalType === JournalTypes.Bank) &&
              // Include credit-side lines too — an in-kind amendment/reversal (e.g. a
              // donor reducing a prior in-kind gift) nets to a credit on the bank side
              // rather than a debit, but still needs the same Expense/885000 reclassification.
              (e.debit !== null || e.credit !== null) &&
              (e.opportunityIds ?? []).some((id) => entryOppIds.has(id)),
          )
        : journalEntries.filter(
            (e) =>
              (e.journalType === JournalTypes.Expense || e.journalType === JournalTypes.Bank) &&
              e.debit !== null &&
              e.debit === entry.credit,
          );

    if (matchingDebits.length === 0) {
      validationEvents.push({
        severity: "error",
        message: `Row ${rowIndex}: Pro-bono Rule, no matching debit entry found in Expense or Bank journal type. You may need to manually add an offsetting entry under an Expense journal type.`,
        field: "debit",
      });
    }
    for (const matchingDebit of matchingDebits) {
      const matchedIndex = journalEntries.indexOf(matchingDebit) + 1;

      if (matchingDebit.journalType !== JournalTypes.Expense) {
        // Modify the matching entry
        matchingDebit.journalType = JournalTypes.Expense;
        matchingDebit.accountNumber = "885000";

        // Copy dimension codes from the revenue entry BEFORE checking completeness below —
        // otherwise the check always sees the entry's pre-copy blank fields and raises a
        // false "fields need populating" error even though this same pass fills them in.
        if (matchingDebit.classId !== "999") {
          matchingDebit.departmentId = entry.departmentId;
          matchingDebit.projectId = entry.projectId;
          matchingDebit.classId = "999";
          matchingDebit.pfpCode = entry.pfpCode;
          matchingDebit.contributionCode = entry.contributionCode;
          validationEvents.push({
            severity: "warning",
            message: `Row ${matchedIndex}: Pro-bono Rule, the Department, Project, Class, PFP & Contribution Code fields has been changed to conform to the rule.`,
            field: "classId",
          });
        }

        if (
          !matchingDebit.departmentId?.trim() ||
          !matchingDebit.projectId?.trim() ||
          !matchingDebit.classId?.trim() ||
          !matchingDebit.pfpCode?.trim() ||
          !matchingDebit.contributionCode?.trim()
        ) {
          validationEvents.push({
            severity: "error",
            message: `Row ${matchedIndex}: Pro-bono Rule, the debit entry found and the journal type has been changed to ${matchingDebit.journalType}, account number amended to 885000. Fields (Department, Project, Class, PFP, Contribution) will now need to be populated to enable posting.`,
            field: "departmentId",
          });
        }
      } else {
        if (matchingDebit.classId !== "999") {
          matchingDebit.classId = "999";
          validationEvents.push({
            severity: "warning",
            message: `Row ${matchedIndex}: Pro-bono Rule, the Class field has been changed to 999.`,
            field: "classId",
          });
        }

        if (
          !matchingDebit.departmentId?.trim() ||
          !matchingDebit.projectId?.trim() ||
          !matchingDebit.classId?.trim() ||
          !matchingDebit.pfpCode?.trim() ||
          !matchingDebit.contributionCode?.trim()
        ) {
          validationEvents.push({
            severity: "error",
            message: `Row ${matchedIndex}: Pro-bono Rule, the debit entry found with journal type ${matchingDebit.journalType} and requires fields (Department, Project, Class, PFP, Contribution) populated. The Class needs to be 999.`,
            field: "departmentId",
          });
        }
      }
    }
  }

  // ===========================================
  // Tax ID Check
  // Only validate tax if taxAmount is present (not 0 or null)
  // ===========================================
  const hasTaxAmount = entry.taxAmount !== null && entry.taxAmount !== 0;
  if (
    hasTaxAmount &&
    (entry.accountNumber === "451000" || entry.accountNumber === "857000") &&
    entry.taxId !== defaultTaxId
  ) {
    entry.taxId = defaultTaxId;
    validationEvents.push({
      severity: "warning",
      message: `Row ${rowIndex}: Missing or incorrect tax id, the tax id of "${defaultTaxId}" has been applied.`,
      field: "taxId",
    });
  }

  // ===========================================
  // Tax Rounding Check
  // Only validate if taxAmount is present (not 0 or null)
  // Debit/Credit are net amounts, so tax should be 10% of that amount
  // ===========================================
  if (hasTaxAmount && (entry.accountNumber === "451000" || entry.accountNumber === "857000")) {
    const debitValue = entry.debit ?? 0;
    const creditValue = entry.credit ?? 0;
    const entryTaxAmount = Math.abs(entry.taxAmount ?? 0);
    const netAmount = debitValue > 0 ? debitValue : creditValue;
    const calculatedTaxAmount = roundToTwoDecimals(netAmount * 0.1);

    if (calculatedTaxAmount !== entryTaxAmount) {
      validationEvents.push({
        severity: "warning",
        message: `Row ${rowIndex}: Rounding error detected, the current tax amount of ${entryTaxAmount} does not match the calculated tax amount of ${calculatedTaxAmount} (10% of net ${netAmount}), please ensure this is correct.`,
        field: "taxAmount",
      });
    }
  }

  // ===========================================
  // Required Field: Account Number
  // ===========================================
  if (!entry.accountNumber?.trim()) {
    validationEvents.push({
      severity: "error",
      message: `Row ${rowIndex}: Account Number is required.`,
      field: "accountNumber",
    });
  }

  // ===========================================
  // Account Number Existence Check
  // Validate that the account number exists in the accounts table
  // ===========================================
  if (entry.accountNumber?.trim() && validAccountNumbers && validAccountNumbers.size > 0) {
    if (!validAccountNumbers.has(entry.accountNumber.trim())) {
      validationEvents.push({
        severity: "error",
        message: `Row ${rowIndex}: Account Number "${entry.accountNumber}" does not exist in the Chart of Accounts. Please select a valid account.`,
        field: "accountNumber",
      });
    }
  }

  // ===========================================
  // Required Field: Debit or Credit
  // ===========================================
  if (
    (entry.debit === null || entry.debit === 0) &&
    (entry.credit === null || entry.credit === 0)
  ) {
    validationEvents.push({
      severity: "error",
      message: `Row ${rowIndex}: Either Debit or Credit must have a value.`,
      field: "debit",
    });
  }

  // ===========================================
  // Revenue/Expense Dimension Code Requirement
  // ===========================================
  if (entry.journalType === JournalTypes.Revenue || entry.journalType === JournalTypes.Expense) {
    if (
      !entry.departmentId?.trim() ||
      !entry.projectId?.trim() ||
      !entry.classId?.trim() ||
      !entry.pfpCode?.trim() ||
      !entry.contributionCode?.trim()
    ) {
      validationEvents.push({
        severity: "error",
        message: `Row ${rowIndex}: Journal Type Rule: ${entry.journalType} journal types must have fields (Department, Project, Class, PFP, Contribution) populated.`,
        field: "departmentId",
      });
    }
  }

  return validationEvents;
}

interface RowValidationResult {
  correctedEntries: JournalEntry[];
  entriesWithErrors: Set<string>;
  entriesWithWarnings: Set<string>;
  rowValidationEvents: Map<string, ValidationEvent[]>;
}

/**
 * Run validateDataRow across every entry, applying auto-corrections (e.g. the
 * Pro-bono rule's Bank->Expense reclassification) and collecting error/warning
 * state per row. Shared by the initial load and the Validate button so both
 * produce identical, already-corrected results.
 */
function runRowValidation(
  sourceEntries: JournalEntry[],
  validAccountNumbers: Set<string>,
  defaultTaxId: string,
): RowValidationResult {
  const entriesCopy = sourceEntries.map((e) => ({ ...e }));
  const entriesWithErrors = new Set<string>();
  const entriesWithWarnings = new Set<string>();
  const rowValidationEvents = new Map<string, ValidationEvent[]>();

  entriesCopy.forEach((entry, index) => {
    const rowIndex = index + 1;
    const events = validateDataRow(entry, rowIndex, entriesCopy, validAccountNumbers, defaultTaxId);

    events.forEach((event) => {
      const rowMatch = event.message.match(/^Row (\d+):/);
      const targetRowIndex = rowMatch ? parseInt(rowMatch[1], 10) : rowIndex;
      const targetEntry = entriesCopy[targetRowIndex - 1];

      if (targetEntry) {
        const existingEvents = rowValidationEvents.get(targetEntry.id) || [];
        if (!existingEvents.some((e) => e.message === event.message)) {
          existingEvents.push(event);
          rowValidationEvents.set(targetEntry.id, existingEvents);
        }
      }
    });
  });

  rowValidationEvents.forEach((events, entryId) => {
    const hasError = events.some((e) => e.severity === "error");
    const hasWarning = events.some((e) => e.severity === "warning");

    if (hasError) {
      entriesWithErrors.add(entryId);
    } else if (hasWarning) {
      entriesWithWarnings.add(entryId);
    }
  });

  return {
    correctedEntries: entriesCopy,
    entriesWithErrors,
    entriesWithWarnings,
    rowValidationEvents,
  };
}

/**
 * Load a set of entries (freshly built from Salesforce, or restored from a
 * saved draft) and run validation twice: the first pass performs
 * auto-corrections (e.g. copying dimension codes onto a freshly-reclassified
 * Expense row), which itself raises a "just changed to conform" warning; the
 * second pass re-validates the now-corrected entries, so that warning doesn't
 * resurface for a state that's already settled.
 */
function buildValidatedEntries(
  rawEntries: JournalEntry[],
  validAccountNumbers: Set<string>,
  defaultTaxId: string,
): RowValidationResult {
  const loadedEntries = rawEntries.map((e) => ({ ...e, isEditing: false }));
  const firstPass = runRowValidation(loadedEntries, validAccountNumbers, defaultTaxId);
  return runRowValidation(firstPass.correctedEntries, validAccountNumbers, defaultTaxId);
}

interface EditingEntry extends JournalEntry {
  isEditing?: boolean;
  originalData?: JournalEntry;
}

/**
 * Sort dropdown items numerically by their value (lowest to highest)
 * Handles string values that represent numbers
 */
function sortDropdownItemsNumerically(items: DropdownItem[] | undefined): DropdownItem[] {
  if (!items) return [];
  return [...items].sort((a, b) => {
    const numA = parseFloat(a.value) || 0;
    const numB = parseFloat(b.value) || 0;
    return numA - numB;
  });
}

export function JournalPostingPage(): JSX.Element {
  const { batchName } = useParams<{ batchName: string }>();
  const navigate = useNavigate();

  // Fetch posting journal type from settings
  const { data: journalTypeSetting } = useQuery({
    queryKey: ["setting", POSTING_JOURNAL_TYPE_KEY],
    queryFn: () => settingsService.getSettingByKey(POSTING_JOURNAL_TYPE_KEY),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch default tax ID from settings
  const { data: defaultTaxIdSetting } = useQuery({
    queryKey: ["setting", DEFAULT_TAX_ID_KEY],
    queryFn: () => settingsService.getSettingByKey(DEFAULT_TAX_ID_KEY),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Header fields - matching original Blazor component
  // Journal type is now controlled by the setting
  const journal = journalTypeSetting?.value || POSTING_JOURNAL_TYPE_DEFAULT;
  const defaultTaxId = defaultTaxIdSetting?.value || DEFAULT_TAX_ID_DEFAULT;
  const [postDate, setPostDate] = useState(new Date().toISOString().split("T")[0]);
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("10");

  // State for journal entries (editable)
  const [entries, setEntries] = useState<EditingEntry[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  // Validation state
  const [entriesWithErrors, setEntriesWithErrors] = useState<Set<string>>(new Set());
  const [entriesWithWarnings, setEntriesWithWarnings] = useState<Set<string>>(new Set());
  const [rowValidationEvents, setRowValidationEvents] = useState<Map<string, ValidationEvent[]>>(
    new Map(),
  );
  const [isPostEnabled, setIsPostEnabled] = useState(false);
  // Track validation state - can be used for UI feedback or preventing post
  const [, setHasValidated] = useState(false);

  // Draft state - lets a user save their in-progress edits and resume later.
  // A non-null lastSavedAt doubles as the "has a saved draft" flag.
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  // Guards against re-initializing entries (and clobbering user edits) if the
  // underlying queries refetch in the background after the initial load
  const hasInitializedRef = useRef(false);

  // Column resize state
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    // Initialize with default widths from column configs
    const widths: Record<string, number> = {};
    COLUMN_CONFIGS.forEach((col) => {
      widths[col.key] = col.defaultWidth;
    });
    return widths;
  });
  const resizingColumn = useRef<string | null>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);

  // Handle column resize move
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!resizingColumn.current) return;

    const columnKey = resizingColumn.current;
    const config = COLUMN_CONFIGS.find((c) => c.key === columnKey);
    if (!config) return;

    const diff = e.clientX - resizeStartX.current;
    const newWidth = Math.max(config.minWidth, resizeStartWidth.current + diff);

    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: newWidth,
    }));
  }, []);

  // Handle column resize end
  const handleResizeEnd = useCallback(() => {
    resizingColumn.current = null;
    document.removeEventListener("mousemove", handleResizeMove);
    document.removeEventListener("mouseup", handleResizeEnd);
  }, [handleResizeMove]);

  // Handle column resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, columnKey: string) => {
      e.preventDefault();
      resizingColumn.current = columnKey;
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = columnWidths[columnKey];

      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
    },
    [columnWidths, handleResizeMove, handleResizeEnd],
  );

  // Fetch journal entries
  const { data, isLoading, error } = useQuery({
    queryKey: ["journal", batchName],
    queryFn: () => journalService.buildJournalEntries(batchName!),
    enabled: !!batchName,
  });

  // Fetch dimension codes for dropdowns
  const { data: dimensionCodesData } = useQuery({
    queryKey: ["dimensionCodes"],
    queryFn: () => journalService.getDimensionCodes(),
  });

  // Fetch any previously saved draft for this batch - a 404 (no draft saved) is
  // an expected outcome, not an error, so retries are disabled
  const draftQuery = useQuery({
    queryKey: ["journalDraft", batchName],
    queryFn: () => journalService.getDraft(batchName!),
    enabled: !!batchName,
    retry: false,
  });

  const dimensionCodes = dimensionCodesData?.data;
  const batchAmount = data?.data?.batchAmount || 0;
  const opportunityCount = data?.data?.opportunityCount || 0;
  const buildBlockingErrors = useMemo(
    () => getBuildBlockingErrors(data?.data?.entries ?? []),
    [data],
  );

  // A refetch can add the error after a clean Validate; only a rebuild clears it
  useEffect(() => {
    if (buildBlockingErrors.length > 0) setIsPostEnabled(false);
  }, [buildBlockingErrors]);

  // Initialize entries and description once both the freshly-built journal and
  // the draft lookup have settled - preferring a saved draft (if one exists)
  // over the freshly-built entries, so a user can resume where they left off.
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!data?.data?.entries) return;
    if (draftQuery.isLoading) return;

    const validAccountNumbers = new Set<string>(
      dimensionCodes?.accountCodes?.map((acc: DropdownItem) => acc.value) || [],
    );

    const draft = draftQuery.data?.data;

    if (draft) {
      const { correctedEntries, entriesWithErrors, entriesWithWarnings, rowValidationEvents } =
        buildValidatedEntries(
          withCurrentBuildErrors(draft.entries, data.data.entries),
          validAccountNumbers,
          defaultTaxId,
        );

      setEntries(correctedEntries);
      setEntriesWithErrors(entriesWithErrors);
      setEntriesWithWarnings(entriesWithWarnings);
      setRowValidationEvents(rowValidationEvents);
      setDescription(draft.description);
      setPostDate(draft.postDate);
      setLocation(draft.location);
      setLastSavedAt(draft.savedAt);
      toast.success("Resumed your saved draft for this batch.");
    } else {
      const { correctedEntries, entriesWithErrors, entriesWithWarnings, rowValidationEvents } =
        buildValidatedEntries(data.data.entries, validAccountNumbers, defaultTaxId);

      setEntries(correctedEntries);
      setEntriesWithErrors(entriesWithErrors);
      setEntriesWithWarnings(entriesWithWarnings);
      setRowValidationEvents(rowValidationEvents);

      if (batchName) {
        setDescription(
          `SF Post ${new Date().toLocaleDateString("en-GB").replace(/\//g, "")} - ${batchName}`,
        );
      }
    }

    hasInitializedRef.current = true;
  }, [data, draftQuery.isLoading, draftQuery.data, batchName, dimensionCodes, defaultTaxId]);

  // Journal types list for dropdown (excluding Tax)
  const journalTypesList = [JournalTypes.Bank, JournalTypes.Revenue, JournalTypes.Expense];

  // Calculate totals matching original Blazor footer
  // All values rounded to 2 decimal places to avoid floating-point precision issues
  const totals = useMemo(() => {
    const totalDebit = roundToTwoDecimals(
      entries.reduce((sum, e) => {
        return sum + (e.debit || 0);
      }, 0),
    );

    const totalCredit = roundToTwoDecimals(
      entries.reduce((sum, e) => {
        return sum + (e.credit || 0);
      }, 0),
    );

    const totalBaseDebit = roundToTwoDecimals(
      entries.reduce((sum, e) => {
        return sum + calculateBaseAmount(e.debit, e.taxAmount);
      }, 0),
    );

    const totalBaseCredit = roundToTwoDecimals(
      entries.reduce((sum, e) => {
        return sum + calculateBaseAmount(e.credit, e.taxAmount);
      }, 0),
    );

    const totalTaxAmount = roundToTwoDecimals(
      entries.reduce((sum, e) => {
        return sum + calculateDisplayedTaxAmount(e.taxAmount);
      }, 0),
    );

    const bankTotal = roundToTwoDecimals(
      entries
        .filter((e) => e.journalType === JournalTypes.Bank)
        .reduce((sum, e) => {
          const debit = e.debit || 0;
          const credit = e.credit || 0;
          return sum + debit - credit;
        }, 0),
    );

    const revenueTotal = roundToTwoDecimals(
      entries
        .filter((e) => e.journalType !== JournalTypes.Bank)
        .reduce((sum, e) => {
          const baseDebit = calculateBaseAmount(e.debit, e.taxAmount);
          const baseCredit = calculateBaseAmount(e.credit, e.taxAmount);
          return sum + baseCredit - baseDebit;
        }, 0),
    );

    const balance = roundToTwoDecimals(totalBaseDebit - totalBaseCredit);

    return {
      totalDebit,
      totalCredit,
      totalBaseDebit,
      totalBaseCredit,
      totalTaxAmount,
      bankTotal,
      revenueTotal,
      balance,
    };
  }, [entries]);

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Post mutation
  const postMutation = useMutation({
    mutationFn: () =>
      journalService.postJournalEntries(
        entries,
        journal,
        description,
        postDate,
        location,
        batchName,
        {
          totalDebit: totals.totalDebit,
          totalCredit: totals.totalCredit,
          bankTotal: totals.bankTotal,
          revenueTotal: totals.revenueTotal,
          balance: totals.balance,
          isBalanced: Math.abs(totals.balance) < 0.01,
        },
        opportunityCount,
        batchAmount,
      ),
    onSuccess: (result) => {
      if (result.data?.success) {
        // Show appropriate message based on whether Sage posting was skipped
        if (result.data?.skippedSagePosting) {
          toast.success(result.data?.message || "Salesforce batch posted date has been updated.");
        } else {
          toast.success("Journal posted successfully!");
        }
        // Invalidate batches cache so the list refreshes with updated posted status
        queryClient.invalidateQueries({ queryKey: ["batches"] });
        // The server deletes the saved draft on a successful post - drop the stale cache entry too
        queryClient.removeQueries({ queryKey: ["journalDraft", batchName] });
        navigate("/batches");
      } else {
        const errorDetails = result.data?.errors?.filter(Boolean) ?? [];
        const postFailureMessage = [
          result.data?.message || "Failed to post journal",
          ...errorDetails,
        ].join("\n");
        toast.error(postFailureMessage, { duration: 10000 });
      }
    },
    onError: (error) => {
      toast.error(`Error: ${errorMessage(error, "Failed to post journal")}`);
    },
  });

  // Save draft mutation - persists current edits so the user can leave and resume later
  const saveDraftMutation = useMutation({
    mutationFn: () =>
      journalService.saveDraft(batchName!, description, postDate, location, entries),
    onSuccess: (result) => {
      setLastSavedAt(result.data?.savedAt ?? new Date().toISOString());
      toast.success("Progress saved. You can leave and resume this journal later.");
    },
    onError: (error) => {
      toast.error(`Failed to save progress: ${errorMessage(error, "Unknown error")}`);
    },
  });

  // Reset mutation - discards the saved draft and any in-progress edits,
  // resetting the journal back to a fresh build from Salesforce
  const resetMutation = useMutation({
    mutationFn: () => journalService.deleteDraft(batchName!),
    onSuccess: () => {
      const validAccountNumbers = new Set<string>(
        dimensionCodes?.accountCodes?.map((acc: DropdownItem) => acc.value) || [],
      );
      const rawEntries = data?.data?.entries || [];
      const { correctedEntries, entriesWithErrors, entriesWithWarnings, rowValidationEvents } =
        buildValidatedEntries(rawEntries, validAccountNumbers, defaultTaxId);

      setEntries(correctedEntries);
      setEntriesWithErrors(entriesWithErrors);
      setEntriesWithWarnings(entriesWithWarnings);
      setRowValidationEvents(rowValidationEvents);
      setDescription(
        batchName
          ? `SF Post ${new Date().toLocaleDateString("en-GB").replace(/\//g, "")} - ${batchName}`
          : "",
      );
      setPostDate(new Date().toISOString().split("T")[0]);
      setLocation("10");
      setLastSavedAt(null);
      setHasValidated(false);
      setIsPostEnabled(false);
      setIsResetConfirmOpen(false);
      queryClient.removeQueries({ queryKey: ["journalDraft", batchName] });
      toast.success("Journal reset. All changes have been discarded.");
    },
    onError: (error) => {
      toast.error(`Failed to reset journal: ${errorMessage(error, "Unknown error")}`);
    },
  });

  // Handle validate button click - comprehensive validation with all business rules
  const handleValidate = useCallback(() => {
    // Build a set of valid account numbers from the accounts table for validation
    const validAccountNumbers = new Set<string>(
      dimensionCodes?.accountCodes?.map((acc: DropdownItem) => acc.value) || [],
    );

    const {
      correctedEntries: entriesCopy,
      entriesWithErrors: newEntriesWithErrors,
      entriesWithWarnings: newEntriesWithWarnings,
      rowValidationEvents: newRowValidationEvents,
    } = runRowValidation(entries, validAccountNumbers, defaultTaxId);

    // Update entries with any modifications made during validation (e.g., Tax ID auto-correction)
    setEntries((prev) =>
      prev.map((entry, index) => ({
        ...entry,
        journalType: entriesCopy[index].journalType,
        accountNumber: entriesCopy[index].accountNumber,
        departmentId: entriesCopy[index].departmentId,
        projectId: entriesCopy[index].projectId,
        classId: entriesCopy[index].classId,
        pfpCode: entriesCopy[index].pfpCode,
        contributionCode: entriesCopy[index].contributionCode,
        taxId: entriesCopy[index].taxId,
      })),
    );

    // Validate header fields
    const headerErrors: string[] = [];
    const headerWarnings: string[] = [];

    if (newEntriesWithErrors.size > 0) {
      headerErrors.push(
        `${newEntriesWithErrors.size} row(s) have errors that must be fixed before posting.`,
      );
    }

    if (newEntriesWithWarnings.size > 0) {
      headerWarnings.push(
        `${newEntriesWithWarnings.size} row(s) have warnings. Review recommended but posting is allowed.`,
      );
    }

    headerErrors.push(...buildBlockingErrors);

    if (!journal.trim()) {
      headerErrors.push("Journal is required.");
    }

    if (!description.trim()) {
      headerErrors.push("Description is required.");
    }

    if (!location.trim()) {
      headerErrors.push("Location is required.");
    }

    // Check if debits equal credits - this is a critical error that blocks posting
    const isBalanced = Math.abs(totals.balance) < 0.01;
    if (!isBalanced) {
      const difference = Math.abs(totals.balance);
      headerErrors.push(
        `Journal entries do not balance! Base Debits: ${formatCurrency(totals.totalBaseDebit)}, Base Credits: ${formatCurrency(totals.totalBaseCredit)}, Difference: ${formatCurrency(difference)}. Please fix the entries before posting.`,
      );
    }

    setEntriesWithErrors(newEntriesWithErrors);
    setEntriesWithWarnings(newEntriesWithWarnings);
    setRowValidationEvents(newRowValidationEvents);
    setHasValidated(true);

    // Update Post button state - only errors block posting, warnings do not
    const canPost = newEntriesWithErrors.size === 0 && headerErrors.length === 0;
    setIsPostEnabled(canPost);

    // Show notifications
    if (headerErrors.length > 0) {
      toast.error(headerErrors.join("\n"), { duration: 8000 });
    }

    if (headerWarnings.length > 0) {
      toast(headerWarnings.join("\n"), {
        duration: 6000,
        icon: "⚠️",
        style: {
          background: "#FFF3CD",
          color: "#856404",
          border: "1px solid #FFEEBA",
        },
      });
    }

    if (headerErrors.length === 0 && headerWarnings.length === 0) {
      if (entries.length === 0) {
        toast.success(
          "Validation passed. No entries to post - only the posted date will be updated in Salesforce.",
        );
      } else {
        toast.success("Validation passed. Journal is balanced and ready to post.");
      }
    } else if (headerErrors.length === 0 && headerWarnings.length > 0) {
      toast.success(
        "Validation passed with warnings. Journal is balanced - you can proceed to post.",
      );
    }
  }, [
    entries,
    journal,
    description,
    location,
    totals,
    dimensionCodes,
    defaultTaxId,
    buildBlockingErrors,
  ]);

  // Handle export to Excel
  const handleExport = useCallback(() => {
    exportJournalToExcel({
      entries,
      totals,
      batchName: batchName || "",
      journal,
      postDate,
      description,
      location,
    });
    toast.success("Journal exported to Excel");
  }, [entries, totals, batchName, journal, postDate, description, location]);

  // Handle save button click - persist current progress
  const handleSave = useCallback(() => {
    if (!batchName) return;
    saveDraftMutation.mutate();
  }, [batchName, saveDraftMutation]);

  // Handle reset button click - confirmed via modal before discarding changes
  const handleReset = useCallback(() => {
    if (!batchName) return;
    resetMutation.mutate();
  }, [batchName, resetMutation]);

  // Handle post button click
  const handlePost = useCallback(() => {
    if (!isPostEnabled) {
      toast.error("Please validate before posting");
      return;
    }

    // Double-check balance before posting
    if (Math.abs(totals.balance) >= 0.01) {
      toast.error(
        `Cannot post: Journal entries do not balance. Base Debits: ${formatCurrency(totals.totalBaseDebit)}, Base Credits: ${formatCurrency(totals.totalBaseCredit)}, Difference: ${formatCurrency(Math.abs(totals.balance))}`,
      );
      setIsPostEnabled(false);
      return;
    }

    postMutation.mutate();
  }, [isPostEnabled, postMutation, totals]);

  // Row editing functions
  const handleEditRow = useCallback((entry: EditingEntry) => {
    setEditingRowId(entry.id);
    setEntries((prev) =>
      prev.map((e) => (e.id === entry.id ? { ...e, isEditing: true, originalData: { ...e } } : e)),
    );
  }, []);

  const handleSaveRow = useCallback(
    (entry: EditingEntry) => {
      setEditingRowId(null);
      setEntries((prev) =>
        prev.map((e) =>
          e.id === entry.id ? { ...e, isEditing: false, originalData: undefined } : e,
        ),
      );

      // Re-validate this row with comprehensive validation
      const currentEntries = entries.map((e) => (e.id === entry.id ? entry : e));
      const index = currentEntries.findIndex((e) => e.id === entry.id);
      const rowIndex = index + 1;

      // Build a set of valid account numbers from the accounts table for validation
      const validAccountNumbers = new Set<string>(
        dimensionCodes?.accountCodes?.map((acc: DropdownItem) => acc.value) || [],
      );

      const events = validateDataRow(
        entry,
        rowIndex,
        currentEntries,
        validAccountNumbers,
        defaultTaxId,
      );

      const newRowValidationEvents = new Map(rowValidationEvents);
      const newEntriesWithErrors = new Set(entriesWithErrors);
      const newEntriesWithWarnings = new Set(entriesWithWarnings);

      // Clear previous validation state for this entry
      newEntriesWithErrors.delete(entry.id);
      newEntriesWithWarnings.delete(entry.id);
      newRowValidationEvents.delete(entry.id);

      // Group events by the row they affect
      const eventsForThisRow: ValidationEvent[] = [];
      events.forEach((event) => {
        const rowMatch = event.message.match(/^Row (\d+):/);
        const targetRowIndex = rowMatch ? parseInt(rowMatch[1], 10) : rowIndex;

        if (targetRowIndex === rowIndex) {
          eventsForThisRow.push(event);
        }
      });

      if (eventsForThisRow.length > 0) {
        newRowValidationEvents.set(entry.id, eventsForThisRow);

        if (eventsForThisRow.some((e) => e.severity === "error")) {
          newEntriesWithErrors.add(entry.id);
        } else if (eventsForThisRow.some((e) => e.severity === "warning")) {
          newEntriesWithWarnings.add(entry.id);
        }
      }

      setRowValidationEvents(newRowValidationEvents);
      setEntriesWithErrors(newEntriesWithErrors);
      setEntriesWithWarnings(newEntriesWithWarnings);
      setHasValidated(false); // Require re-validation after edit
      setIsPostEnabled(false); // Disable post until full re-validation
    },
    [
      entries,
      rowValidationEvents,
      entriesWithErrors,
      entriesWithWarnings,
      dimensionCodes,
      defaultTaxId,
    ],
  );

  const handleCancelRow = useCallback((entry: EditingEntry) => {
    setEditingRowId(null);

    // If this is a new row (no original data), remove it entirely
    if (!entry.originalData) {
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    } else {
      // Restore original data for existing rows
      setEntries((prev) =>
        prev.map((e) =>
          e.id === entry.id ? { ...e.originalData!, isEditing: false, originalData: undefined } : e,
        ),
      );
    }
  }, []);

  const handleDeleteRow = useCallback((entry: EditingEntry) => {
    setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    setEntriesWithErrors((prev) => {
      const next = new Set(prev);
      next.delete(entry.id);
      return next;
    });
    setEntriesWithWarnings((prev) => {
      const next = new Set(prev);
      next.delete(entry.id);
      return next;
    });
    setRowValidationEvents((prev) => {
      const next = new Map(prev);
      next.delete(entry.id);
      return next;
    });
    setHasValidated(false);
  }, []);

  const handleInsertRow = useCallback(
    (afterEntry: EditingEntry) => {
      const index = entries.findIndex((e) => e.id === afterEntry.id);
      const newEntry: EditingEntry = {
        id: `NEW-${Date.now()}`,
        journalType: afterEntry.journalType,
        memo: "",
        accountNumber: "",
        accountName: "",
        departmentId: "",
        projectId: "",
        classId: "",
        pfpCode: "",
        contributionCode: "",
        debit: null,
        credit: null,
        taxId: "",
        taxAmount: null,
        description: "",
        validations: [],
        isEditing: true,
      };

      const newEntries = [...entries];
      newEntries.splice(index + 1, 0, newEntry);
      setEntries(newEntries);
      setEditingRowId(newEntry.id);
      setHasValidated(false);
    },
    [entries],
  );

  const handleFieldChange = useCallback(
    (entryId: string, field: keyof JournalEntry, value: JournalEntry[keyof JournalEntry]) => {
      setEntries((prev) => prev.map((e) => (e.id === entryId ? { ...e, [field]: value } : e)));
    },
    [],
  );

  // Format currency
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  };

  // Get row background color based on validation state
  const getRowClassName = (entry: EditingEntry): string => {
    if (entriesWithErrors.has(entry.id)) {
      return "bg-red-100 dark:bg-red-900/20";
    }
    if (entriesWithWarnings.has(entry.id)) {
      return "bg-orange-100 dark:bg-orange-900/20";
    }
    return "";
  };

  // Get sticky cell background class based on validation state
  const getStickyCellBgClass = (entry: EditingEntry): string => {
    if (entriesWithErrors.has(entry.id)) {
      return "bg-red-100 dark:bg-red-900/20";
    }
    if (entriesWithWarnings.has(entry.id)) {
      return "bg-orange-100 dark:bg-orange-900/20";
    }
    return "bg-white dark:bg-gray-900";
  };

  // Get tooltip for row
  const getRowTooltip = (entry: EditingEntry): string | undefined => {
    const events = rowValidationEvents.get(entry.id);
    if (!events || events.length === 0) return undefined;
    return events.map((e) => e.message).join("; ");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center text-red-600 dark:text-red-400">
          <p>Error building journal: {errorMessage(error, "Failed to build journal")}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col h-[90vh] overflow-hidden">
      {/* Header Section */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center space-x-4 mb-4">
          <Button variant="ghost" onClick={() => navigate("/batches")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Post Opportunity Batch
          </h1>
        </div>

        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
          Batch ID: {batchName}
        </h4>

        {/* Header Fields - matching original Blazor layout */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <label className="text-gray-600 dark:text-gray-400">Journal:</label>
            <input
              type="text"
              value={journal}
              readOnly
              className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100 bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
              title="Configured in Settings > Opportunities Posting"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-gray-600 dark:text-gray-400">Post Date:</label>
            <input
              type="date"
              value={postDate}
              onChange={(e) => setPostDate(e.target.value)}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-gray-600 dark:text-gray-400">Description:</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-72 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-gray-600 dark:text-gray-400">Location:</label>
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-48 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
            >
              {dimensionCodes?.locationCodes?.map((loc: DropdownItem) => (
                <option key={loc.value} value={loc.value}>
                  {loc.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-gray-600 dark:text-gray-400">Batch Amount:</label>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatCurrency(batchAmount)}
            </span>
          </div>
        </div>
      </div>

      {/* Data Grid Section */}
      <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md flex flex-col h-full">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Journal Entries ({entries.length}) - {opportunityCount} opportunities
            </h3>
          </div>

          {/* Scrollable Table Area */}
          <div className="flex-1 overflow-auto pb-4 min-h-0">
            <table
              className="text-sm"
              style={{
                tableLayout: "fixed",
                borderCollapse: "separate",
                borderSpacing: 0,
                minWidth: "max-content",
              }}
            >
              <thead className="sticky top-0" style={{ zIndex: 40 }}>
                <tr>
                  {COLUMN_CONFIGS.map((col) => {
                    const stickyPositions = getStickyLeftPositions(columnWidths);
                    const isSticky = col.sticky;
                    const isLastSticky = col.key === LAST_STICKY_KEY;
                    const colWidth = columnWidths[col.key];

                    return (
                      <th
                        key={col.key}
                        className={`px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase relative select-none bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 ${
                          col.align === "right" ? "text-right" : "text-left"
                        }`}
                        style={{
                          width: colWidth,
                          minWidth: colWidth,
                          maxWidth: colWidth,
                          ...(isSticky
                            ? {
                                position: "sticky",
                                left: stickyPositions[col.key],
                                zIndex: 50,
                                boxShadow: isLastSticky
                                  ? "4px 0 8px -4px rgba(0, 0, 0, 0.15)"
                                  : undefined,
                              }
                            : {
                                zIndex: 40,
                              }),
                        }}
                      >
                        <span className="truncate block">{col.label}</span>
                        {/* Resize handle */}
                        <div
                          className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-400/50 active:bg-blue-500/50"
                          onMouseDown={(e) => handleResizeStart(e, col.key)}
                          title="Drag to resize column"
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <tr
                    key={entry.id}
                    className={`${getRowClassName(entry)} border-b border-gray-200 dark:border-gray-700`}
                  >
                    {/* Actions Column */}
                    <td
                      className={`px-3 py-2 whitespace-nowrap overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{
                        width: columnWidths.actions,
                        minWidth: columnWidths.actions,
                        maxWidth: columnWidths.actions,
                        position: "sticky",
                        left: getStickyLeftPositions(columnWidths).actions,
                        zIndex: 10,
                      }}
                    >
                      {entry.isEditing ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleSaveRow(entry)}
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            title="Save"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleCancelRow(entry)}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleInsertRow(entry)}
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            title="Insert Row"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditRow(entry)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                            title="Edit"
                            disabled={editingRowId !== null}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRow(entry)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Row Number */}
                    <td
                      className={`px-3 py-2 text-gray-900 dark:text-gray-100 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{
                        width: columnWidths.row,
                        minWidth: columnWidths.row,
                        maxWidth: columnWidths.row,
                        position: "sticky",
                        left: getStickyLeftPositions(columnWidths).row,
                        zIndex: 10,
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <span>{index + 1}</span>
                        {entriesWithErrors.has(entry.id) && (
                          <span title={getRowTooltip(entry)} className="flex-shrink-0">
                            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                          </span>
                        )}
                        {!entriesWithErrors.has(entry.id) && entriesWithWarnings.has(entry.id) && (
                          <span title={getRowTooltip(entry)} className="flex-shrink-0">
                            <AlertTriangle className="h-4 w-4 text-orange-500 dark:text-orange-400" />
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Journal Type */}
                    <td
                      className={`px-3 py-2 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{
                        width: columnWidths.journalType,
                        minWidth: columnWidths.journalType,
                        maxWidth: columnWidths.journalType,
                        position: "sticky",
                        left: getStickyLeftPositions(columnWidths).journalType,
                        zIndex: 10,
                      }}
                    >
                      {entry.isEditing ? (
                        <select
                          value={entry.journalType}
                          onChange={(e) =>
                            handleFieldChange(
                              entry.id,
                              "journalType",
                              e.target.value as JournalTypes,
                            )
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700"
                        >
                          {journalTypesList.map((jt) => (
                            <option key={jt} value={jt}>
                              {jt}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100 truncate block">
                          {entry.journalType}
                        </span>
                      )}
                    </td>

                    {/* Memo */}
                    <td
                      className={`px-3 py-2 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{
                        width: columnWidths.memo,
                        minWidth: columnWidths.memo,
                        maxWidth: columnWidths.memo,
                        position: "sticky",
                        left: getStickyLeftPositions(columnWidths).memo,
                        zIndex: 10,
                      }}
                    >
                      {entry.isEditing ? (
                        <input
                          type="text"
                          value={entry.memo}
                          onChange={(e) => handleFieldChange(entry.id, "memo", e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700"
                        />
                      ) : (
                        <span
                          className="text-gray-900 dark:text-gray-100 truncate block"
                          title={entry.memo}
                        >
                          {entry.memo}
                        </span>
                      )}
                    </td>

                    {/* Account Number */}
                    <td
                      className={`px-3 py-2 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{
                        width: columnWidths.accountNumber,
                        minWidth: columnWidths.accountNumber,
                        maxWidth: columnWidths.accountNumber,
                        position: "sticky",
                        left: getStickyLeftPositions(columnWidths).accountNumber,
                        zIndex: 10,
                        boxShadow: "4px 0 8px -4px rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      {entry.isEditing ? (
                        <ComboBox
                          options={dimensionCodes?.accountCodes || []}
                          value={entry.accountNumber}
                          onChange={(value) => handleFieldChange(entry.id, "accountNumber", value)}
                          placeholder="Select or type..."
                          className="w-full"
                        />
                      ) : (
                        <span
                          className="text-gray-900 dark:text-gray-100 truncate block"
                          title={
                            dimensionCodes?.accountCodes?.find(
                              (a: DropdownItem) => a.value === entry.accountNumber,
                            )?.label || entry.accountNumber
                          }
                        >
                          {entry.accountNumber}
                        </span>
                      )}
                    </td>

                    {/* Department ID */}
                    <td
                      className={`px-3 py-2 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.departmentId }}
                    >
                      {entry.isEditing ? (
                        <select
                          value={entry.departmentId}
                          onChange={(e) =>
                            handleFieldChange(entry.id, "departmentId", e.target.value)
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700"
                        >
                          <option value="">-- Select --</option>
                          {sortDropdownItemsNumerically(dimensionCodes?.departmentCodes).map(
                            (d: DropdownItem) => (
                              <option key={d.value} value={d.value}>
                                {d.label}
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <span
                          className="text-gray-900 dark:text-gray-100 truncate block"
                          title={
                            dimensionCodes?.departmentCodes?.find(
                              (d: DropdownItem) => d.value === entry.departmentId,
                            )?.label || entry.departmentId
                          }
                        >
                          {dimensionCodes?.departmentCodes?.find(
                            (d: DropdownItem) => d.value === entry.departmentId,
                          )?.label || entry.departmentId}
                        </span>
                      )}
                    </td>

                    {/* Project ID */}
                    <td
                      className={`px-3 py-2 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.projectId }}
                    >
                      {entry.isEditing ? (
                        <select
                          value={entry.projectId}
                          onChange={(e) => handleFieldChange(entry.id, "projectId", e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700"
                        >
                          <option value="">-- Select --</option>
                          {sortDropdownItemsNumerically(dimensionCodes?.projectCodes).map(
                            (p: DropdownItem) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <span
                          className="text-gray-900 dark:text-gray-100 truncate block"
                          title={
                            dimensionCodes?.projectCodes?.find(
                              (p: DropdownItem) => p.value === entry.projectId,
                            )?.label || entry.projectId
                          }
                        >
                          {dimensionCodes?.projectCodes?.find(
                            (p: DropdownItem) => p.value === entry.projectId,
                          )?.label || entry.projectId}
                        </span>
                      )}
                    </td>

                    {/* Class ID */}
                    <td
                      className={`px-3 py-2 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.classId }}
                    >
                      {entry.isEditing ? (
                        <select
                          value={entry.classId}
                          onChange={(e) => handleFieldChange(entry.id, "classId", e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700"
                        >
                          <option value="">-- Select --</option>
                          {sortDropdownItemsNumerically(dimensionCodes?.classCodes).map(
                            (c: DropdownItem) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <span
                          className="text-gray-900 dark:text-gray-100 truncate block"
                          title={
                            dimensionCodes?.classCodes?.find(
                              (c: DropdownItem) => c.value === entry.classId,
                            )?.label || entry.classId
                          }
                        >
                          {dimensionCodes?.classCodes?.find(
                            (c: DropdownItem) => c.value === entry.classId,
                          )?.label || entry.classId}
                        </span>
                      )}
                    </td>

                    {/* PFP Code */}
                    <td
                      className={`px-3 py-2 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.pfpCode }}
                    >
                      {entry.isEditing ? (
                        <select
                          value={entry.pfpCode}
                          onChange={(e) => handleFieldChange(entry.id, "pfpCode", e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700"
                        >
                          <option value="">-- Select --</option>
                          {sortDropdownItemsNumerically(dimensionCodes?.pfpCodes).map(
                            (p: DropdownItem) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <span
                          className="text-gray-900 dark:text-gray-100 truncate block"
                          title={
                            dimensionCodes?.pfpCodes?.find(
                              (p: DropdownItem) => p.value === entry.pfpCode,
                            )?.label || entry.pfpCode
                          }
                        >
                          {dimensionCodes?.pfpCodes?.find(
                            (p: DropdownItem) => p.value === entry.pfpCode,
                          )?.label || entry.pfpCode}
                        </span>
                      )}
                    </td>

                    {/* Contribution Code */}
                    <td
                      className={`px-3 py-2 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.contributionCode }}
                    >
                      {entry.isEditing ? (
                        <select
                          value={entry.contributionCode}
                          onChange={(e) =>
                            handleFieldChange(entry.id, "contributionCode", e.target.value)
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700"
                        >
                          <option value="">-- Select --</option>
                          {dimensionCodes?.contributionCodes?.map((c: DropdownItem) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className="text-gray-900 dark:text-gray-100 truncate block"
                          title={
                            dimensionCodes?.contributionCodes?.find(
                              (c: DropdownItem) => c.value === entry.contributionCode,
                            )?.label || entry.contributionCode
                          }
                        >
                          {dimensionCodes?.contributionCodes?.find(
                            (c: DropdownItem) => c.value === entry.contributionCode,
                          )?.label || entry.contributionCode}
                        </span>
                      )}
                    </td>

                    {/* Debit */}
                    <td
                      className={`px-3 py-2 text-right overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.debit }}
                    >
                      {entry.isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={
                            entry.debit !== null && entry.debit !== undefined
                              ? entry.debit.toFixed(2)
                              : ""
                          }
                          onChange={(e) =>
                            handleFieldChange(
                              entry.id,
                              "debit",
                              e.target.value ? parseFloat(e.target.value) : null,
                            )
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-right dark:bg-gray-700"
                        />
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100 truncate block">
                          {entry.debit && entry.debit > 0 ? formatCurrency(entry.debit) : "-"}
                        </span>
                      )}
                    </td>

                    {/* Credit */}
                    <td
                      className={`px-3 py-2 text-right overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.credit }}
                    >
                      {entry.isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={
                            entry.credit !== null && entry.credit !== undefined
                              ? entry.credit.toFixed(2)
                              : ""
                          }
                          onChange={(e) =>
                            handleFieldChange(
                              entry.id,
                              "credit",
                              e.target.value ? parseFloat(e.target.value) : null,
                            )
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-right dark:bg-gray-700"
                        />
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100 truncate block">
                          {entry.credit && entry.credit > 0 ? formatCurrency(entry.credit) : "-"}
                        </span>
                      )}
                    </td>

                    {/* Base Debit */}
                    <td
                      className={`px-3 py-2 text-right overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.baseDebit }}
                    >
                      <span className="text-gray-900 dark:text-gray-100 truncate block">
                        {entry.debit && entry.debit > 0
                          ? formatCurrency(calculateBaseAmount(entry.debit, entry.taxAmount))
                          : "-"}
                      </span>
                    </td>

                    {/* Base Credit */}
                    <td
                      className={`px-3 py-2 text-right overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.baseCredit }}
                    >
                      <span className="text-gray-900 dark:text-gray-100 truncate block">
                        {entry.credit && entry.credit > 0
                          ? formatCurrency(calculateBaseAmount(entry.credit, entry.taxAmount))
                          : "-"}
                      </span>
                    </td>

                    {/* Tax ID */}
                    <td
                      className={`px-3 py-2 overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.taxId }}
                    >
                      {entry.isEditing ? (
                        <input
                          type="text"
                          value={entry.taxId}
                          onChange={(e) => handleFieldChange(entry.id, "taxId", e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700"
                        />
                      ) : (
                        <span
                          className="text-gray-900 dark:text-gray-100 truncate block"
                          title={entry.taxId}
                        >
                          {entry.taxId}
                        </span>
                      )}
                    </td>

                    {/* Tax Amount */}
                    <td
                      className={`px-3 py-2 text-right overflow-hidden ${getStickyCellBgClass(entry)}`}
                      style={{ width: columnWidths.taxAmount }}
                    >
                      {entry.isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={
                            entry.taxAmount !== null && entry.taxAmount !== undefined
                              ? entry.taxAmount.toFixed(2)
                              : ""
                          }
                          onChange={(e) =>
                            handleFieldChange(
                              entry.id,
                              "taxAmount",
                              e.target.value ? parseFloat(e.target.value) : null,
                            )
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-right dark:bg-gray-700"
                        />
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100 truncate block">
                          {entry.taxAmount
                            ? formatCurrency(calculateDisplayedTaxAmount(entry.taxAmount))
                            : "-"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer with totals - matching original Blazor layout */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex-shrink-0 bg-gray-50 dark:bg-gray-900">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <div className="flex flex-wrap gap-6">
                <span className="text-gray-600 dark:text-gray-400">
                  Bank Total:{" "}
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(totals.bankTotal)}
                  </span>
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  Revenue Total:{" "}
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(totals.revenueTotal)}
                  </span>
                </span>
              </div>
              <div className="flex flex-wrap gap-6">
                <span className="text-gray-600 dark:text-gray-400">
                  Base Debits:{" "}
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(totals.totalBaseDebit)}
                  </span>
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  Base Credits:{" "}
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(totals.totalBaseCredit)}
                  </span>
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  Tax Subtotal:{" "}
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(totals.totalTaxAmount)}
                  </span>
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  Balance:{" "}
                  <span
                    className={`font-semibold ${Math.abs(totals.balance) < 0.01 ? "text-green-600" : "text-red-600"}`}
                  >
                    {formatCurrency(totals.balance)}
                  </span>
                  {Math.abs(totals.balance) < 0.01 && (
                    <Check className="inline h-4 w-4 ml-1 text-green-600" />
                  )}
                  {Math.abs(totals.balance) >= 0.01 && (
                    <AlertTriangle className="inline h-4 w-4 ml-1 text-red-600" />
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons - matching original Blazor layout */}
      <div className="flex-shrink-0 flex items-center justify-end mt-4 gap-3">
        {lastSavedAt && (
          <span className="text-xs text-gray-500 dark:text-gray-400 mr-auto">
            Progress saved at {new Date(lastSavedAt).toLocaleTimeString()}
          </span>
        )}
        <Button
          variant="secondary"
          onClick={() => navigate("/batches")}
          className="w-28"
          title="Leave this screen and return to the batch list"
        >
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={() => setIsResetConfirmOpen(true)}
          disabled={resetMutation.isPending}
          className="w-28"
          title="Discard all changes and saved progress, and rebuild the journal fresh from Salesforce"
        >
          <RotateCcw className="h-4 w-4 mr-1" />
          Reset
        </Button>
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={entries.length === 0}
          className="w-28"
          title="Export the current journal entries to an Excel file"
        >
          <Download className="h-4 w-4 mr-1" />
          Export
        </Button>
        <Button
          variant="outline"
          onClick={handleSave}
          isLoading={saveDraftMutation.isPending}
          className="w-28"
          title="Save your progress so you can leave and resume this journal later"
        >
          <Save className="h-4 w-4 mr-1" />
          Save
        </Button>
        <Button
          variant="primary"
          onClick={handleValidate}
          className="w-28"
          title="Check the journal for errors and warnings before posting"
        >
          Validate
        </Button>
        <Button
          variant="danger"
          onClick={handlePost}
          disabled={!isPostEnabled}
          isLoading={postMutation.isPending}
          className="w-28"
          title="Post the validated journal to Sage Intacct"
        >
          Post
        </Button>
      </div>

      {/* Reset confirmation */}
      <Modal
        isOpen={isResetConfirmOpen}
        onClose={() => setIsResetConfirmOpen(false)}
        title="Reset this journal?"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setIsResetConfirmOpen(false)}
              disabled={resetMutation.isPending}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReset} isLoading={resetMutation.isPending}>
              Reset
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Are you sure? All unsaved changes and any saved progress for this batch will be lost, and
          the journal will be rebuilt fresh from Salesforce.
        </p>
      </Modal>
    </div>
  );
}
